import { Fragment, useCallback, useEffect, useMemo } from 'react';
import { isArray, isEmpty } from 'lodash/fp';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'app/lib/hooks';
import { doListJobEnvironments, selectEnvironments } from 'app/store/jobs';
import { Button, ButtonSize, ButtonVariant } from 'app/components/core/Button';
import { Field, useFormContext } from 'app/components/core/Form';
import { Icon, IconSize } from 'app/components/core/Icon';
import { Select } from 'app/components/core/Select';
import { useDialogContext } from 'app/components/features/DialogWithChapters';
import * as styles from './JobEnvironmentField.css';

function EmptyStateMessage() {
    const { t } = useTranslation();

    return (
        <div className={styles.empty}>
            <Icon size={IconSize.EXTRA_SMALL} variant="system" />
            {t('jobs:environments.empty')}
        </div>
    );
}

export function JobEnvironmentField({ name = 'environmentUid' }) {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const environments = useSelector(selectEnvironments);
    const { getFieldValue, setFieldValue } = useFormContext();
    const { onEditEnvironment } = useDialogContext();

    const value = getFieldValue(name);

    useEffect(() => {
        dispatch(doListJobEnvironments());
    }, [dispatch]);

    const options = useMemo(
        () => environments.map((env) => ({ value: env.uid, label: env.name })),
        [environments]
    );

    const environment = useMemo(
        () => environments?.find((env) => env.uid === value),
        [environments, value]
    );

    const handleCreate = useCallback(() => {
        onEditEnvironment({
            onCreate: (uid) => setFieldValue(name, uid)
        });
    }, [onEditEnvironment, setFieldValue, name]);

    const handleEdit = useCallback(() => {
        onEditEnvironment({ environment });
    }, [onEditEnvironment, environment]);

    const hasOptions = isArray(options) && !isEmpty(options);

    return (
        <Fragment>
            {hasOptions ? (
                <Field
                    component={Select}
                    label={t('jobs:definitions.environment')}
                    placeholder={t('jobs:definitions.environment_placeholder')}
                    name={name}
                    options={options}
                />
            ) : (
                <EmptyStateMessage />
            )}
            <div className={styles.buttons}>
                <Button
                    icon="add"
                    variant={ButtonVariant.SECONDARY_BLUE}
                    size={ButtonSize.SMALL}
                    onClick={handleCreate}>
                    {t('jobs:environments.create')}
                </Button>
                {hasOptions && (
                    <Button
                        icon="edit"
                        variant={ButtonVariant.SECONDARY}
                        size={ButtonSize.SMALL}
                        disabled={isEmpty(value) || isEmpty(environment)}
                        onClick={handleEdit}>
                        {t('jobs:environments.edit')}
                    </Button>
                )}
            </div>
        </Fragment>
    );
}
